<script setup lang="ts">
import { computed } from "vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";

// Import shared components
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Image from "@/views/blocks/shared/section/image/Image.vue";
import LinkedInIcon from "@/views/blocks/shared/section/media/icons/LinkedInIcon.vue";
import XIcon from "@/views/blocks/shared/section/media/icons/XIcon.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";

import type { WithVerticalImagesProps } from "./WithVerticalImagesProps";

const props = defineProps<{
    block: PageBlock<WithVerticalImagesProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <FullSpacing>
            <div class="lg:max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-5 gap-20">
                <!-- Left: Title & Description -->
                <div class="xl:col-span-2 flex flex-col max-w-2xl">
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :textColor="styleValues.text"
                            class="text-4xl lg:text-5xl mb-6"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :textColor="styleValues.text"
                            class="text-lg/8 opacity-70"
                        />
                    </EditableElement>
                </div>
                <!-- Right: Team Members -->
                <div class="xl:col-span-3 flex flex-col">
                    <EditableElement
                        v-for="(member, index) in dataValues.teamMembers.data.items"
                        :key="member.id"
                        :blockId="block.id"
                        :field-name="`teamMembers_${member.id}`"
                        :hideable="false"
                    >
                        <div
                            class="border-t flex flex-col sm:flex-row gap-10 py-12"
                            :class="{
                                'border-t-0 pt-0': index === 0,
                                'pb-0': index === dataValues.teamMembers.data.items.length - 1,
                            }"
                            :style="{ borderColor: styleValues.border }"
                        >
                            <Image
                                :src="member.imageSrc"
                                :alt="member.name"
                                class="aspect-[4/5] max-w-full w-52 rounded-lg object-cover"
                                width="208"
                                height="260"
                            />
                            <div class="max-w-xl">
                                <Paragraph
                                    :text="member.name"
                                    :textColor="styleValues.text"
                                    class="text-lg/8 font-semibold"
                                />
                                <Paragraph
                                    :text="member.role"
                                    :textColor="styleValues.text"
                                    class="text-base/7 opacity-70"
                                />
                                <Paragraph
                                    :text="member.description"
                                    :textColor="styleValues.text"
                                    class="text-base/7 mt-6 opacity-70"
                                />
                                <div class="flex gap-6 mt-6">
                                    <a
                                        v-if="member.linkedInLink"
                                        :href="member.linkedInLink"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="link opacity-80"
                                    >
                                        <span class="sr-only">LinkedIn</span>
                                        <LinkedInIcon class="w-5 h-5" />
                                    </a>
                                    <a
                                        v-if="member.xLink"
                                        :href="member.xLink"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="link opacity-80"
                                    >
                                        <span class="sr-only">X</span>
                                        <XIcon class="w-5 h-5" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </EditableElement>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>

<style scoped>
.link {
    color: v-bind("styleValues.text");
}

.link:hover {
    color: color-mix(in srgb, v-bind("styleValues.text") 90%, transparent);
}

a:focus-visible {
    outline: 2px solid v-bind("styleValues.text");
    outline-offset: 2px;
}
</style>
